import { Paragraph } from '@customerio/pluma-components/react';
import type { Customer } from '@/services/db/customer/entity';
import { customerDisplay } from '@/services/db/customer/display';
import { objectDisplayName, type ObjectRecord } from '@/services/db/object/entity';
import type { ObjectType } from '@/services/db/object-type/entity';

import { formatNumberAndPluralize } from '@/utils/number';

type ReviewSummaryInput = {
  people: readonly Customer[];
  objects: readonly ObjectRecord[];
  objectType: Pick<ObjectType, 'singular_name'>;
  attributeCount: number;
  /** Workspace term for a person, singular. */
  peopleSingular: string;
};

/** Names one side of the relationship: the record itself when it is the only one. */
function target(count: number, only: string | undefined, singular: string): string {
  return count === 1 && only != null ? only : formatNumberAndPluralize(count, singular);
}

export function reviewSummary(input: ReviewSummaryInput): string {
  const isPlural = input.people.length > 1 || input.objects.length > 1;
  const customers = target(
    input.people.length,
    input.people[0] == null ? undefined : customerDisplay(input.people[0], input.peopleSingular),
    input.peopleSingular,
  );
  const objects = target(
    input.objects.length,
    input.objects[0] == null ? undefined : objectDisplayName(input.objects[0], input.objectType),
    input.objectType.singular_name,
  );

  return [
    `You are about to add ${isPlural ? 'relationships' : 'a relationship'} between ${customers} and ${objects}.`,
    ...(input.attributeCount > 0
      ? [`${formatNumberAndPluralize(input.attributeCount, 'attribute')} will be added to each relationship.`]
      : []),
    `Each selected ${input.peopleSingular} will be related to each selected account. Any existing relationships will not be re-added or duplicated.`,
  ].join(' ');
}

export function ReviewStep(props: ReviewSummaryInput) {
  return (
    <Paragraph className="fs-mask" my="200" data-test-add-relationships-review>
      {reviewSummary(props)}
    </Paragraph>
  );
}
